import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, Volume2, Headphones, Filter, FileText, Clock, Calendar, Timer, Hourglass, Activity, BarChart3, PieChart, LineChart, TrendingUp, TrendingDown, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ArrowUpRight, ArrowDownRight, ArrowUpLeft, ArrowDownLeft, Maximize, Minimize, Maximize2, Minimize2, RotateCw, RotateCcw, RefreshCw, RefreshCcw, Undo, Redo, Copy, Scissors, Clipboard, ClipboardCheck, ClipboardCopy, ClipboardList, ClipboardPaste, ClipboardX, File, FileCheck, FileEdit, FileMinus, FilePlus, FileSearch, FileText as FileTextIcon, FileX, Folder, FolderCheck, FolderEdit, FolderMinus, FolderOpen, FolderPlus, FolderSearch, FolderX, Sun, Moon, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudHail, CloudFog, CloudSun, CloudMoon, Sunrise, Sunset, StarHalf, StarOff, Sparkle, Sparkles, Diamond, Gem, Coins, Banknote, CreditCard, Wallet, PiggyBank, ShoppingCart, ShoppingBag, Package, Truck, Home, Building, Square, Circle, Triangle, Hexagon, Octagon, Pentagon, Type, Contrast, Focus, Target, Brain, BookOpen, Clock as ClockIcon, Calendar as CalendarIcon, Timer as TimerIcon, Hourglass as HourglassIcon, Activity as ActivityIcon, PieChart as PieChartIcon, LineChart as LineChartIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, ArrowUp as ArrowUpIcon, ArrowDown as ArrowDownIcon, ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon, ArrowUpRight as ArrowUpRightIcon, ArrowDownRight as ArrowDownRightIcon, ArrowUpLeft as ArrowUpLeftIcon, ArrowDownLeft as ArrowDownLeftIcon, Maximize as MaximizeIcon, Minimize as MinimizeIcon, Maximize2 as Maximize2Icon, Minimize2 as Minimize2Icon, RotateCw as RotateCwIcon, RotateCcw as RotateCcwIcon, RefreshCw as RefreshCwIcon, RefreshCcw as RefreshCcwIcon, Undo as UndoIcon, Redo as RedoIcon, Copy as CopyIcon, Scissors as ScissorsIcon, Clipboard as ClipboardIcon, ClipboardCheck as ClipboardCheckIcon, ClipboardCopy as ClipboardCopyIcon, ClipboardList as ClipboardListIcon, ClipboardPaste as ClipboardPasteIcon, ClipboardX as ClipboardXIcon, File as FileIcon, FileCheck as FileCheckIcon, FileEdit as FileEditIcon, FileMinus as FileMinusIcon, FilePlus as FilePlusIcon, FileSearch as FileSearchIcon, FileTextIcon as FileTextIconAlias, FileX as FileXIcon, Folder as FolderIcon, FolderCheck as FolderCheckIcon, FolderEdit as FolderEditIcon, FolderMinus as FolderMinusIcon, FolderOpen as FolderOpenIcon, FolderPlus as FolderPlusIcon, FolderSearch as FolderSearchIcon, FolderX as FolderXIcon, Sun as SunIcon, Moon as MoonIcon, Cloud as CloudIcon, CloudRain as CloudRainIcon, CloudSnow as CloudSnowIcon, CloudLightning as CloudLightningIcon, CloudDrizzle as CloudDrizzleIcon, CloudHail as CloudHailIcon, CloudFog as CloudFogIcon, CloudSun as CloudSunIcon, CloudMoon as CloudMoonIcon, Sunrise as SunriseIcon, Sunset as SunsetIcon, StarHalf as StarHalfIcon, StarOff as StarOffIcon, Sparkle as SparkleIcon, Sparkles as SparklesIcon, Diamond as DiamondIcon, Gem as GemIcon, Coins as CoinsIcon, Banknote as BanknoteIcon, CreditCard as CreditCardIcon, Wallet as WalletIcon, PiggyBank as PiggyBankIcon, ShoppingCart as ShoppingCartIcon, ShoppingBag as ShoppingBagIcon, Package as PackageIcon, Truck as TruckIcon, Home as HomeIcon, Building as BuildingIcon, Square as SquareIcon, Circle as CircleIcon, Triangle as TriangleIcon, Hexagon as HexagonIcon, Octagon as OctagonIcon, Pentagon as PentagonIcon } from 'lucide-react';

interface AccessibilitySettings {
  screenReader: boolean;
  highContrast: boolean;
  largeText: boolean;
  reducedMotion: boolean;
  focusIndicators: boolean;
  keyboardNavigation: boolean;
  colorBlindSupport: boolean;
  dyslexiaSupport: boolean;
  voiceControl: boolean;
  magnification: number;
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
  fontFamily: 'sans' | 'serif' | 'mono' | 'dyslexia';
  lineHeight: 'tight' | 'normal' | 'relaxed' | 'loose';
  letterSpacing: 'tight' | 'normal' | 'wide' | 'wider';
  wordSpacing: 'tight' | 'normal' | 'wide' | 'wider';
  colorScheme: 'default' | 'high-contrast' | 'dark' | 'sepia' | 'blue-light';
  focusRing: boolean;
  skipLinks: boolean;
  landmarks: boolean;
  altText: boolean;
  captions: boolean;
  transcripts: boolean;
  audioDescriptions: boolean;
}

interface AccessibilityFeaturesProps {
  onSettingsChange?: (settings: AccessibilitySettings) => void;
  onVoiceCommand?: (command: string) => void;
  onFocusChange?: (element: string) => void;
}

const AccessibilityFeatures: React.FC<AccessibilityFeaturesProps> = ({
  onSettingsChange,
  onVoiceCommand,
  onFocusChange
}) => {
  const [settings, setSettings] = useState<AccessibilitySettings>({
    screenReader: false,
    highContrast: false,
    largeText: false,
    reducedMotion: false,
    focusIndicators: true,
    keyboardNavigation: true,
    colorBlindSupport: false,
    dyslexiaSupport: false,
    voiceControl: false,
    magnification: 100,
    fontSize: 'medium',
    fontFamily: 'sans',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    wordSpacing: 'normal',
    colorScheme: 'default',
    focusRing: true,
    skipLinks: true,
    landmarks: true,
    altText: true,
    captions: true,
    transcripts: true,
    audioDescriptions: true
  });

  const [isListening, setIsListening] = useState(false);
  const [voiceCommands, setVoiceCommands] = useState<string[]>([]);
  const [currentFocus, setCurrentFocus] = useState<string>('');
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState<'visual' | 'audio' | 'motor' | 'cognitive'>('visual');

  const recognitionRef = useRef<any>(null);
  const focusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event: any) => {
        const command = event.results[event.results.length - 1][0].transcript.toLowerCase();
        setVoiceCommands(prev => [...prev, command]);
        onVoiceCommand?.(command);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    // Apply accessibility settings to document
    applyAccessibilitySettings();

    // Set up focus tracking
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [settings]);

  const applyAccessibilitySettings = () => {
    const root = document.documentElement;
    
    // Apply color scheme
    root.setAttribute('data-color-scheme', settings.colorScheme);
    
    // Apply high contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Apply reduced motion
    if (settings.reducedMotion) {
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('reduced-motion');
    }
    
    // Apply dyslexia support
    if (settings.dyslexiaSupport) {
      root.classList.add('dyslexia-support');
    } else {
      root.classList.remove('dyslexia-support');
    }
    
    // Apply magnification
    root.style.setProperty('zoom', `${settings.magnification}%`);
    
    // Apply font settings
    root.style.setProperty('--font-size', settings.fontSize);
    root.style.setProperty('--font-family', settings.fontFamily);
    root.style.setProperty('--line-height', settings.lineHeight);
    root.style.setProperty('--letter-spacing', settings.letterSpacing);
    root.style.setProperty('--word-spacing', settings.wordSpacing);
    
    // Apply focus indicators
    if (settings.focusIndicators) {
      root.classList.add('focus-indicators');
    } else {
      root.classList.remove('focus-indicators');
    }
    
    // Apply skip links
    if (settings.skipLinks) {
      root.classList.add('skip-links');
    } else {
      root.classList.remove('skip-links');
    }
    
    // Apply landmarks
    if (settings.landmarks) {
      root.classList.add('landmarks');
    } else {
      root.classList.remove('landmarks');
    }
    
    onSettingsChange?.(settings);
  };

  const handleFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement;
    if (target) {
      setCurrentFocus(target.getAttribute('aria-label') || target.tagName || 'Unknown element');
      onFocusChange?.(currentFocus);
    }
  };

  const handleFocusOut = (event: FocusEvent) => {
    setCurrentFocus('');
  };

  const handleSettingChange = (key: keyof AccessibilitySettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applyAccessibilitySettings();
  };

  const startVoiceControl = () => {
    if (recognitionRef.current && settings.voiceControl) {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const stopVoiceControl = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const handleVoiceCommand = (command: string) => {
    const cmd = command.toLowerCase().trim();
    
    if (cmd.includes('increase') && cmd.includes('text')) {
      const sizes = ['small', 'medium', 'large', 'xlarge'];
      const currentIndex = sizes.indexOf(settings.fontSize);
      if (currentIndex < sizes.length - 1) {
        handleSettingChange('fontSize', sizes[currentIndex + 1]);
      }
    } else if (cmd.includes('decrease') && cmd.includes('text')) {
      const sizes = ['small', 'medium', 'large', 'xlarge'];
      const currentIndex = sizes.indexOf(settings.fontSize);
      if (currentIndex > 0) {
        handleSettingChange('fontSize', sizes[currentIndex - 1]);
      }
    } else if (cmd.includes('high contrast')) {
      handleSettingChange('highContrast', !settings.highContrast);
    } else if (cmd.includes('screen reader')) {
      handleSettingChange('screenReader', !settings.screenReader);
    } else if (cmd.includes('dyslexia')) {
      handleSettingChange('dyslexiaSupport', !settings.dyslexiaSupport);
    }
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'visual':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Visual Accessibility
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    High Contrast Mode
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.highContrast}
                    onChange={(e) => handleSettingChange('highContrast', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Large Text
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.largeText}
                    onChange={(e) => handleSettingChange('largeText', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Color Blind Support
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.colorBlindSupport}
                    onChange={(e) => handleSettingChange('colorBlindSupport', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Dyslexia Support
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.dyslexiaSupport}
                    onChange={(e) => handleSettingChange('dyslexiaSupport', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Font Size: {settings.fontSize}
                  </label>
                  <select
                    value={settings.fontSize}
                    onChange={(e) => handleSettingChange('fontSize', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="xlarge">Extra Large</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Font Family: {settings.fontFamily}
                  </label>
                  <select
                    value={settings.fontFamily}
                    onChange={(e) => handleSettingChange('fontFamily', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="sans">Sans Serif</option>
                    <option value="serif">Serif</option>
                    <option value="mono">Monospace</option>
                    <option value="dyslexia">Dyslexia Friendly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Line Height: {settings.lineHeight}
                  </label>
                  <select
                    value={settings.lineHeight}
                    onChange={(e) => handleSettingChange('lineHeight', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="tight">Tight</option>
                    <option value="normal">Normal</option>
                    <option value="relaxed">Relaxed</option>
                    <option value="loose">Loose</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Color Scheme: {settings.colorScheme}
                  </label>
                  <select
                    value={settings.colorScheme}
                    onChange={(e) => handleSettingChange('colorScheme', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="default">Default</option>
                    <option value="high-contrast">High Contrast</option>
                    <option value="dark">Dark</option>
                    <option value="sepia">Sepia</option>
                    <option value="blue-light">Blue Light Filter</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Magnification: {settings.magnification}%
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="200"
                    step="10"
                    value={settings.magnification}
                    onChange={(e) => handleSettingChange('magnification', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Audio Accessibility
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Screen Reader Support
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.screenReader}
                    onChange={(e) => handleSettingChange('screenReader', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Voice Control
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.voiceControl}
                    onChange={(e) => handleSettingChange('voiceControl', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Audio Descriptions
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.audioDescriptions}
                    onChange={(e) => handleSettingChange('audioDescriptions', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Captions
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.captions}
                    onChange={(e) => handleSettingChange('captions', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Transcripts
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.transcripts}
                    onChange={(e) => handleSettingChange('transcripts', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                {settings.voiceControl && (
                  <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Voice Control
                      </span>
                      <button
                        onClick={isListening ? stopVoiceControl : startVoiceControl}
                        className={`px-4 py-2 rounded-lg text-white ${
                          isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                      >
                        {isListening ? 'Stop Listening' : 'Start Listening'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Try saying: "increase text", "high contrast", "screen reader"
                    </p>
                    {voiceCommands.length > 0 && (
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        Recent commands: {voiceCommands.slice(-3).join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'motor':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Motor Accessibility
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Keyboard Navigation
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.keyboardNavigation}
                    onChange={(e) => handleSettingChange('keyboardNavigation', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Focus Indicators
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.focusIndicators}
                    onChange={(e) => handleSettingChange('focusIndicators', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Focus Ring
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.focusRing}
                    onChange={(e) => handleSettingChange('focusRing', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Skip Links
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.skipLinks}
                    onChange={(e) => handleSettingChange('skipLinks', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Reduced Motion
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.reducedMotion}
                    onChange={(e) => handleSettingChange('reducedMotion', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'cognitive':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Cognitive Accessibility
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Landmarks
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.landmarks}
                    onChange={(e) => handleSettingChange('landmarks', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Alt Text
                  </label>
                  <input
                    type="checkbox"
                    checked={settings.altText}
                    onChange={(e) => handleSettingChange('altText', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Letter Spacing: {settings.letterSpacing}
                  </label>
                  <select
                    value={settings.letterSpacing}
                    onChange={(e) => handleSettingChange('letterSpacing', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="tight">Tight</option>
                    <option value="normal">Normal</option>
                    <option value="wide">Wide</option>
                    <option value="wider">Wider</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Word Spacing: {settings.wordSpacing}
                  </label>
                  <select
                    value={settings.wordSpacing}
                    onChange={(e) => handleSettingChange('wordSpacing', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="tight">Tight</option>
                    <option value="normal">Normal</option>
                    <option value="wide">Wide</option>
                    <option value="wider">Wider</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Accessibility Features
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Customize your reading experience for better accessibility and inclusion
        </p>
      </div>

      {/* Current Focus Indicator */}
      {currentFocus && (
        <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-lg mb-6">
          <span className="text-sm font-medium">Currently focused: {currentFocus}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'visual', label: 'Visual', icon: Eye },
              { id: 'audio', label: 'Audio', icon: Volume2 },
              { id: 'motor', label: 'Motor', icon: Target },
              { id: 'cognitive', label: 'Cognitive', icon: Brain }
            ].map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <IconComponent className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {getTabContent()}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => handleSettingChange('highContrast', !settings.highContrast)}
            className={`p-4 rounded-lg border-2 transition-colors ${
              settings.highContrast
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Contrast className="h-6 w-6 mx-auto mb-2" />
            <span className="text-sm font-medium">High Contrast</span>
          </button>

          <button
            onClick={() => handleSettingChange('largeText', !settings.largeText)}
            className={`p-4 rounded-lg border-2 transition-colors ${
              settings.largeText
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Type className="h-6 w-6 mx-auto mb-2" />
            <span className="text-sm font-medium">Large Text</span>
          </button>

          <button
            onClick={() => handleSettingChange('screenReader', !settings.screenReader)}
            className={`p-4 rounded-lg border-2 transition-colors ${
              settings.screenReader
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Headphones className="h-6 w-6 mx-auto mb-2" />
            <span className="text-sm font-medium">Screen Reader</span>
          </button>

          <button
            onClick={() => handleSettingChange('dyslexiaSupport', !settings.dyslexiaSupport)}
            className={`p-4 rounded-lg border-2 transition-colors ${
              settings.dyslexiaSupport
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <BookOpen className="h-6 w-6 mx-auto mb-2" />
            <span className="text-sm font-medium">Dyslexia Support</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityFeatures;
